import { watch, ref, computed } from 'vue'
import {
  OUTFIT_FIRST_PAGE,
  OUTFIT_ITEMS_PER_PAGE,
} from '../constants/pagination'
import { useRoute } from '#app/composables/router'
import useCms from '~/composables/cms/useCms'
import type {
  SbImageText,
  SbOutfit,
  SbOutfitOverview,
  StoryblokStory,
} from '~/cms/storyblok/types/storyblok.d.ts'

type PaginatedOutfitResponseData = {
  stories: StoryblokStory<SbOutfit[] | SbOutfitOverview[]>
  perPage: number
  total: number
}

const useOutfitsFolder = () => {
  const route = useRoute()
  const {
    data: outfitsOverviewData,
    fetchBySlug,
    fetching,
  } = useCms<SbOutfitOverview>('cmsOutfitOverviewData')
  const { data: cmsOutfitsFolderData, fetchByFolder } =
    useCms<PaginatedOutfitResponseData>('cmsOutfitsFolderData')

  const fetch = async (
    page: number = OUTFIT_FIRST_PAGE,
    perPage: number = OUTFIT_ITEMS_PER_PAGE,
  ) => {
    if (!outfitsOverviewData.value?.slug) {
      await fetchBySlug('outfits', { contentType: 'OutfitOverview' })
    }

    itemsPerPage.value = perPage

    await fetchByFolder('outfits', {
      page,
      perPage,
      contentType: 'Outfit',
    })
  }

  // Get outfits data
  const outfitsData = computed<SbImageText[]>(() => {
    if (cmsOutfitsFolderData.value && 'stories' in cmsOutfitsFolderData.value) {
      return (
        (cmsOutfitsFolderData.value.stories as StoryblokStory<SbOutfit>[])
          ?.filter(
            (cmsOutfitsPageData: StoryblokStory<SbOutfit>) =>
              cmsOutfitsPageData?.content?.component === 'Outfit',
          )
          .map((outfitsData): SbImageText => {
            return {
              Image: outfitsData?.content?.ImageOverview,
              _uid: outfitsData?.content?._uid,
              ctaLabel: outfitsData?.content?.ctaLabel,
              ctaLink: {
                cached_url: outfitsData.full_slug,
                linktype: 'story',
              },
              headline: outfitsData?.content?.headline,
              textArea: outfitsData?.content?.textArea,
              component: 'ImageText',
            }
          }) || []
      )
    } else {
      return [] // Ensure a return value if 'stories' is not present
    }
  })

  const itemsPerPage = ref(OUTFIT_ITEMS_PER_PAGE)
  const currentPage = computed(() => {
    return Number(route.query.page as string) || OUTFIT_FIRST_PAGE
  })
  const lastPage = computed(() => {
    const totalItems =
      cmsOutfitsFolderData.value && 'total' in cmsOutfitsFolderData.value
        ? cmsOutfitsFolderData.value?.total
        : 0
    return Math.ceil(Number(totalItems) / itemsPerPage.value)
  })

  watch(currentPage, async (newCurrentPage) => {
    await fetch(newCurrentPage)
  })

  return {
    outfitsData,
    outfitsOverviewData,
    cmsOutfitsFolderData,
    fetch,
    currentPage,
    lastPage,
    fetching,
  }
}

export default useOutfitsFolder
